import React, { useCallback, FC, useContext, ReactNode } from 'react';
import TimePicker from 'rc-time-picker';
import styled from 'styled-components';
import moment from 'moment';
import each from 'lodash/each';
import find from 'lodash/find';
import isNumber from 'lodash/isNumber';
import get from 'lodash/get';
import partial from 'lodash/partial';
import partialRight from 'lodash/partialRight';
import 'rc-time-picker/assets/index.css';

import ControlContainer, { ControlLabel } from '@ad-scripts/presentation/components/common/forms/control_container';

import FormContext from 'src/presentation/contexts/FormContext';
import getErrorMessage from 'src/presentation/components/forms/getErrorMessage';
import { Checkbox } from 'src/presentation/components/agent_desktop_components';
import {
  getDisabledStartHours,
  getDisabledStartMinutes,
  getDisabledStopHours,
  getDisabledStopMinutes,
  isTwelveAm,
  splitTime,
} from 'src/presentation/components/pages/shared/utils/TimeUtils';
import { RowLabel, CustomRow } from 'src/presentation/components/pages/chat/ChatLayout';
import { TIME_24H, TIME_AM_PM } from 'src/presentation/components/pages/shared/TimeConstants';

const EMPTY_STATE = [];
const INITIAL_STATE = { start: '', stop: '' };
const DEFAULT_START_TIME = '09:00';
const DEFAULT_START_TIME_PLACEHOLDER = '09:00 AM';
const DEFAULT_END_TIME = '17:00';
const DEFAULT_END_TIME_PLACEHOLDER = '05:00 PM';
const DEFAULT_STATE = { start: DEFAULT_START_TIME, stop: DEFAULT_END_TIME };

export enum IntervalPart {
  START,
  END,
}

type TimeRangeProps = {
  attr: string | null;
  dayData: any;
  label: string | ReactNode;
  onTimeRangeChanged: Function;
};

interface TimeInputProps {
  'data-aid'?: string;
  attr: string;
  className?: string;
  disabled: boolean;
  disabledHours?: Function;
  disabledMinutes?: Function;
  fullAttr: string;
  hasError: boolean;
  hideAmSelector?: boolean;
  hidePmSelector?: boolean;
  intervalPart: IntervalPart;
  label?: string;
  onChange: Function;
  value: string;
}

const TimeRange = ({ attr, dayData, label, onTimeRangeChanged }: TimeRangeProps) => {
  const { resetError, errors } = useContext(FormContext);

  const closed = !(dayData ?? []).length;
  const onTimeChange = useCallback(
    (event: { attr: 'start' | 'stop'; fullAttr: string; value: string }, range: { start?: string; stop?: string }) => {
      const { attr: relativeRangeAttr, fullAttr, value } = event;

      resetError({ attr: fullAttr, errors });

      const newValue = [{ ...range, [relativeRangeAttr]: value }];
      onTimeRangeChanged({ attr, value: newValue });
    },
    [attr, errors, onTimeRangeChanged, resetError]
  );

  const toggleClosedState = useCallback(
    ({ attr, checked }) => {
      onTimeRangeChanged({ attr, value: checked ? EMPTY_STATE : [DEFAULT_STATE] });

      // Reset all errors of all children's ranges
      each(errors, error => {
        const errorAttr = get(error, 'attr', '');
        if (errorAttr.startsWith(attr)) {
          resetError({ attr: errorAttr, errors });
        }
      });
    },
    [errors, resetError, onTimeRangeChanged]
  );

  const ranges = dayData
    ? dayData.map((range, index) => {
        const rangeAttr = `${attr}[${index}]`;
        const startAttr = `${rangeAttr}.start`;
        const stopAttr = `${rangeAttr}.stop`;

        const errorStart = find(errors, error => error.attr === startAttr);
        const errorStop = find(errors, error => error.attr === stopAttr);

        const errorBothEndsEmpty =
          errorStart && errorStop && errorStart.code === errorStop.code && errorStop.code === 'blank';

        return (
          <InputRanges
            attr={rangeAttr}
            disabled={closed}
            errorBothEndsEmpty={errorBothEndsEmpty}
            errorStart={errorStart}
            errorStop={errorStop}
            key={rangeAttr}
            onTimeChange={partialRight(onTimeChange, range)}
            row={range}
            startAttr={startAttr}
            stopAttr={stopAttr}
          />
        );
      })
    : [];

  if (closed) {
    ranges.push(ClosedInputRange({ attr }));
  }

  return (
    <TimeRangesSelectRow data-aid={`${attr}-row`} key={attr}>
      <DayLabel>{label}</DayLabel>
      {ranges}
      <CheckBoxWrapper>
        <Checkbox
          data-aid={`${attr}-closed`}
          checked={closed}
          onChange={() => toggleClosedState({ attr, checked: !closed })}
          label="Closed"
          value={closed ? 'checked' : ''}
        />
      </CheckBoxWrapper>
    </TimeRangesSelectRow>
  );
};

const TimeRangesSelectRow = styled(CustomRow)`
  margin-bottom: 0;
`;

const ClosedInputRange: FC<any> = ({ attr }) => {
  return (
    <InputRanges
      key={`${attr}[0]`}
      row={INITIAL_STATE}
      disabled={true}
      startAttr={`${attr}[0].start`}
      stopAttr={`${attr}[0].stop`}
    />
  );
};

export const InputRanges: FC<any> = ({
  'data-aid': dataAid,
  attr,
  disabled,
  errorBothEndsEmpty,
  errorOverlap,
  errorStart,
  errorStop,
  onTimeChange,
  row,
  startAttr,
  stopAttr,
  stopAttrName = 'stop',
  separatorText = 'to',
  startLabel,
  stopLabel,
  ...otherParams
}) => {
  const { start: startTime, [stopAttrName]: stopTime } = row;
  const { hours: selectedStartHour, minutes: selectedStartMinutes } = splitTime(startTime);
  const { hours: selectedStopHour, minutes: selectedStopMinutes } = splitTime(stopTime);

  const hideStartPmSelector =
    stopTime &&
    !isTwelveAm(stopTime) &&
    ((isNumber(selectedStopHour) && selectedStopHour < 12) || stopTime === '12:00');
  const hideStopPmSelector = startTime === '23:59';

  const rangeData = {
    startTime,
    stopTime,
    selectedStartHour,
    selectedStartMinutes,
    selectedStopHour,
    selectedStopMinutes,
  };

  const showErrorMessage = errorStart || errorStop || errorBothEndsEmpty || errorOverlap;
  let errorMessage;

  const getErrorMessageForRange = () => {
    if (errorBothEndsEmpty) {
      return 'Both start time and end time need to be filled';
    }

    if (errorOverlap) {
      return errorOverlap;
    }

    return getErrorMessage(errorStart || errorStop, '');
  };

  if (showErrorMessage) {
    errorMessage = getErrorMessageForRange();
  }

  const hasStartErrorState = !!errorStart || errorBothEndsEmpty || errorOverlap;
  const hasStopErrorState = !!errorStop || errorBothEndsEmpty || errorOverlap;

  return (
    <TimeRangeWrapper>
      <StartStopSelectorsWrapper>
        <TimeSelector
          attr="start"
          data-aid={dataAid ? `${dataAid}-start` : ''}
          disabled={disabled}
          disabledHours={partial(getDisabledStartHours, rangeData)}
          disabledMinutes={partial(getDisabledStartMinutes, rangeData)}
          fullAttr={startAttr}
          hasError={hasStartErrorState}
          hidePmSelector={hideStartPmSelector}
          intervalPart={IntervalPart.START}
          key={startAttr}
          label={startLabel}
          onChange={onTimeChange}
          value={startTime}
          {...otherParams}
        />
        <ToSeparator>{separatorText}</ToSeparator>
        <TimeSelector
          attr="stop"
          data-aid={dataAid ? `${dataAid}-stop` : ''}
          disabled={disabled}
          disabledHours={partial(getDisabledStopHours, rangeData)}
          disabledMinutes={partial(getDisabledStopMinutes, rangeData)}
          fullAttr={stopAttr}
          hasError={hasStopErrorState}
          hidePmSelector={hideStopPmSelector}
          intervalPart={IntervalPart.END}
          key={stopAttr}
          label={stopLabel}
          onChange={onTimeChange}
          value={stopTime}
          {...otherParams}
        />
      </StartStopSelectorsWrapper>
      {showErrorMessage ? (
        <ErrorWrapper>
          <ErrorLabel data-aid={`${attr}-error`}>{errorMessage}</ErrorLabel>
        </ErrorWrapper>
      ) : null}
    </TimeRangeWrapper>
  );
};

export const TimeSelector = ({
  'data-aid': dataAid,
  attr,
  className,
  disabled,
  disabledHours,
  disabledMinutes,
  fullAttr,
  hasError,
  hideAmSelector,
  hidePmSelector,
  intervalPart,
  label,
  onChange,
  value,
  ...otherProps
}: TimeInputProps) => {
  const wrappedValue = moment(value, [TIME_24H]);
  const isStartTimeSelector = intervalPart === IntervalPart.START;

  const onTimeSelectorChange = value => {
    const valueToSet = intervalPart === IntervalPart.END && isTwelveAm(value) ? '24:00' : value.format(TIME_24H);
    onChange({ attr, fullAttr, value: valueToSet });
  };

  const placeholder = isStartTimeSelector ? DEFAULT_START_TIME_PLACEHOLDER : DEFAULT_END_TIME_PLACEHOLDER;
  const defaultOpenValue = isStartTimeSelector
    ? moment(DEFAULT_START_TIME_PLACEHOLDER, [TIME_AM_PM])
    : moment(DEFAULT_END_TIME_PLACEHOLDER, [TIME_AM_PM]);

  return (
    <FieldContainer
      className={`${className}-container`}
      label={label}
      data-aid={`${dataAid || fullAttr}-container`}
      control={
        <StyledTimeSelectBox className={className} data-aid={dataAid || fullAttr}>
          <StyledTimePicker
            attr={attr}
            className={className}
            defaultOpenValue={defaultOpenValue}
            disabled={disabled}
            disabledHours={disabledHours}
            disabledMinutes={disabledMinutes}
            hasError={hasError}
            hideAmSelector={hideAmSelector}
            hidePmSelector={hidePmSelector}
            hideDisabledOptions
            onChange={onTimeSelectorChange}
            placeholder={placeholder}
            value={wrappedValue.isValid() ? wrappedValue : null}
            {...otherProps}
          />
        </StyledTimeSelectBox>
      }
    />
  );
};

const TimePickerWrapper = ({ className, ...props }) => (
  // @ts-ignore
  <TimePicker
    {...props}
    allowEmpty={false}
    className={className}
    format="hh:mm A"
    inputReadOnly
    minuteStep={1}
    popupClassName={className}
    showSecond={false}
    use12Hours
  />
);

export const FieldContainer = styled(ControlContainer)`
  ${ControlLabel} {
    margin-bottom: 0;
    min-height: auto;
  }
`;

export const StyledTimeSelectBox = styled.div`
  position: relative;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
`;

const TimeRangeWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StartStopSelectorsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const ErrorWrapper = styled.div`
  position: relative;
  margin-bottom: 18px;
`;

const DayLabel = styled(RowLabel)`
  min-width: 85px;
  text-transform: capitalize;
`;

const CheckBoxWrapper = styled.div`
  margin-top: 0.8em;
  position: relative;
  padding-left: 10px;
`;

export const ErrorLabel = styled.div`
  position: absolute;
  min-width: 350px;
  color: ${p => p.theme.colors.red400};
`;

export const ToSeparator = styled(RowLabel)`
  padding: 0 10px;
  line-height: 0.9;
`;

export const StyledTimePicker = styled(TimePickerWrapper)`
  & .rc-time-picker-input,
  & .rc-time-picker-panel-inner {
    font-size: 13px;
    color: ${p => p.theme.colors.black};
  }
  & .rc-time-picker-input {
    ${p => p.hasError && `border-color: ${p.theme.colors.red400}`};
    ${p => p.height && `height: ${p.height}`};
  }
  & .rc-time-picker-panel-select-option-selected {
    background: ${p => p.theme.colors.green400};
    color: ${p => p.theme.colors.white};
  }

  & .rc-time-picker-panel-select {
    li:hover {
      background: ${p => p.theme.colors.green100};
      color: ${p => p.theme.colors.black};
    }
    li:focus {
      outline: none;
    }

    ::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  }

  // Show/Hide AM/PM buttons
  ${p =>
    (p.hideAmSelector || p.hidePmSelector) &&
    `
      .rc-time-picker-panel-select:last-child ul li:${p.hideAmSelector ? 'first-child' : 'last-child'} {
        display: none;
      }
    `};
`;

export default TimeRange;
